import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from "lucide-react";
import { GMAIL_REVIEWS } from "../data/content";
interface GmailCardProps {
  name: string;
  email: string;
  subject: string;
  body: string;
}
function GmailCard({
  name,
  email,
  subject,
  body
}: GmailCardProps) {
  const initial = name.charAt(0).toUpperCase();
  return <article className="min-h-[182px] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm" data-id="element-124" data-mp="nwa7l4q8a3qu">
      <div className="flex items-center gap-3" data-id="element-125" data-mp="rydls14a7mft">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white" data-id="element-126" data-mp="s087hhndniky">
          {initial}
        </div>
        <div className="min-w-0 flex-1" data-id="element-127" data-mp="j4ir5n0sdeef">
          <p className="truncate text-sm font-semibold text-gray-900" data-id="element-128" data-mp="j3h40k6hx1gc">{name}</p>
          <p className="truncate text-xs text-gray-500" data-id="element-129" data-mp="16u3c2bgi2lqc">{email}</p>
        </div>
        <div className="flex gap-0.5" aria-label="5 de 5 estrellas" data-id="element-130" data-mp="1bmwdapcrfbxo">
          {Array.from({
          length: 5
        }).map((_, index) => <StarIcon key={index} size={12} aria-hidden="true" className="fill-yellow-400 text-yellow-400" data-id="element-131" data-mp="3adnwr1o7ozpv" data-mpi={index} />)}
        </div>
      </div>
      <p className="mt-3 text-sm font-semibold text-gray-900" data-id="element-132" data-mp="3fettdakzt74o">{subject}</p>
      <p className="mt-1 text-sm leading-relaxed text-gray-600" data-id="element-133" data-mp="3fdinn57zlpax">{body}</p>
    </article>;
}
export function GmailReviews() {
  const [activeIndex, setActiveIndex] = useState(0);
  const totalReviews = GMAIL_REVIEWS.length;
  const goToReview = (index: number) => {
    setActiveIndex((index + totalReviews) % totalReviews);
  };
  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: {
    offset: {
      x: number;
    };
  }) => {
    if (info.offset.x < -45) goToReview(activeIndex + 1);
    if (info.offset.x > 45) goToReview(activeIndex - 1);
  };
  return <section className="px-5 pt-10" aria-label="Reseñas de clientes" data-id="element-134" data-mp="23sv4ji7kx25v">
      <div className="relative" data-id="element-135" data-mp="7oyvjvnpiook">
        <div className="overflow-hidden rounded-2xl" data-id="element-136" data-mp="7q067z0t995r">
          <motion.div className="flex" animate={{
          x: `-${activeIndex * 100}%`
        }} transition={{
          type: "spring",
          stiffness: 320,
          damping: 32
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.12} onDragEnd={handleDragEnd} data-id="element-137" data-mp="cmqlwl8pe2qn">
            {GMAIL_REVIEWS.map(review => <div key={review.email} className="w-full flex-none" data-id="element-138" data-mp="vm4ilaxthrku">
                <GmailCard {...review} data-id="element-139" data-mp="vka003a0qbjx" />
              </div>)}
          </motion.div>
        </div>

        <button type="button" onClick={() => goToReview(activeIndex - 1)} aria-label="Ver reseña anterior" className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-blue-100 bg-white/95 text-blue-700 shadow-sm transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2" data-id="element-140" data-mp="10exldt9q6pua">
          <ChevronLeftIcon size={19} aria-hidden="true" data-id="element-141" data-mp="2tv218xwwdbjo" />
        </button>
        <button type="button" onClick={() => goToReview(activeIndex + 1)} aria-label="Ver siguiente reseña" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-blue-100 bg-white/95 text-blue-700 shadow-sm transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2" data-id="element-142" data-mp="2txwjqjwcomag">
          <ChevronRightIcon size={19} aria-hidden="true" data-id="element-143" data-mp="38sayu9csnbju" />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="Seleccionar reseña" data-id="element-144" data-mp="33tzjjj3qc8u8">
        {GMAIL_REVIEWS.map((review, index) => <button key={review.email} type="button" role="tab" aria-selected={activeIndex === index} aria-label={`Ver reseña ${index + 1} de ${totalReviews}`} onClick={() => goToReview(index)} className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${activeIndex === index ? "w-6 bg-blue-700" : "w-2.5 bg-blue-200 hover:bg-blue-400"}`} data-id="element-145" data-mp="55u4lxzo5usd" data-mpi={index} />)}
      </div>

      <div className="mt-6 flex flex-col items-center" data-id="element-146" data-mp="3kza1qquvji0">
        <div className="flex gap-1" aria-label="Valoración media: 4.9 de 5 estrellas" data-id="element-147" data-mp="3nlnu84cav8o">
          {Array.from({
          length: 5
        }).map((_, index) => <StarIcon key={index} size={26} aria-hidden="true" className="fill-yellow-400 text-yellow-400" data-id="element-148" data-mp="3q1vb2dwj9x7v" data-mpi={index} />)}
        </div>
        <p className="mt-2 text-lg font-extrabold text-blue-950" data-id="element-149" data-mp="mk3badc5o8z4">4.9/5</p>
      </div>
    </section>;
}